import { useRef, useCallback, useState, useEffect } from 'react';
import { TARIFF_CARD_GAP_PX } from './BusinessTariffs.constants';

const SWIPE_THRESHOLD_PX = 50;

function getContentWidth(el: HTMLDivElement | null): number {
  if (!el) return 0;
  const style = getComputedStyle(el);
  const paddingLeft = parseFloat(style.paddingLeft) || 0;
  const paddingRight = parseFloat(style.paddingRight) || 0;
  return el.clientWidth - paddingLeft - paddingRight;
}

export function useTariffCarousel(
  viewportRef: React.RefObject<HTMLDivElement | null>,
  tariffNames: string[],
  selectedTariff: string,
  setSelectedTariff: (tariff: string) => void,
  mobileBreakpointPx: number
): {
  translatePx: number;
  trackWidthPx: number;
  cardWidthPx: number;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
} {
  const startX = useRef(0);
  const [contentWidth, setContentWidth] = useState(0);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setContentWidth(getContentWidth(el));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewportRef]);

  const selectedIndex = Math.max(0, tariffNames.indexOf(selectedTariff));
  const count = tariffNames.length;
  const cardWidthPx = contentWidth;
  const trackWidthPx =
    count > 0 ? count * cardWidthPx + (count - 1) * TARIFF_CARD_GAP_PX : 0;
  const translatePx =
    count > 0 ? -selectedIndex * (cardWidthPx + TARIFF_CARD_GAP_PX) : 0;

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (
        typeof window === 'undefined' ||
        window.innerWidth > mobileBreakpointPx
      ) {
        return;
      }
      startX.current = e.touches[0].clientX;
    },
    [mobileBreakpointPx]
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (
        typeof window === 'undefined' ||
        window.innerWidth > mobileBreakpointPx ||
        count <= 1
      ) {
        return;
      }
      const endX = e.changedTouches[0].clientX;
      const dx = endX - startX.current;
      const prevTariff = tariffNames[selectedIndex - 1];
      const nextTariff = tariffNames[selectedIndex + 1];
      if (dx > SWIPE_THRESHOLD_PX && selectedIndex > 0 && prevTariff) {
        setSelectedTariff(prevTariff);
      } else if (
        dx < -SWIPE_THRESHOLD_PX &&
        selectedIndex < count - 1 &&
        nextTariff
      ) {
        setSelectedTariff(nextTariff);
      }
    },
    [count, mobileBreakpointPx, selectedIndex, tariffNames, setSelectedTariff]
  );

  return {
    translatePx,
    trackWidthPx,
    cardWidthPx,
    onTouchStart,
    onTouchEnd,
  };
}
